// WARNING: DO NOT IMPORT THIS FILE DIRECTLY
// WARNING: ALWAYS IMPORT @/utils/logger INSTEAD
// WARNING: THIS FILE MAY CRASH AND BURN IN SSR

import { IS_SERVER } from '@/constants/runtime'
import { ShwEnv } from '@/constants/types'

import { WEBSITE_ENV } from '@/client/settings'

import { getDatadogLogger as getDatadogLoggerOrig } from '@/vendor/datadogLogs'

if (IS_SERVER) {
  throw new Error(
    'Unexpected server-runtime in client logger. Did you mean to import @/utils/logger instead?'
  )
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error'
const CURRENT_LOG_LEVEL: LogLevel = 'info'
// ^ Note: Change this to affect what logs are displayed

let userId: number | null = null

const logToDatadog = WEBSITE_ENV !== ShwEnv.DEVELOPMENT
const logToConsole = WEBSITE_ENV !== ShwEnv.PRODUCTION

const datadogLogger = logToDatadog ? getDatadogLoggerOrig(CURRENT_LOG_LEVEL) : null

// In JS, the "Error" class does not have any enumerable properties
// which means that Object.keys(new Error()) returns empty, which means
// that by default, for json logs, we'll be logging an empty object in
// place of an error this function replaces errors with their properties
// copy-pastaed
function normalizeDetails(
  details: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (details == null) {
    return details
  }

  const newDetails: typeof details = {}
  Object.keys(details).forEach(key => {
    const value = details[key]
    if (value instanceof Error) {
      // eslint-disable-next-line no-param-reassign
      newDetails[key] = {
        ...value,
        // ^ All enumerable properties
        level: 'error',
        name: value.name,
        message: value.message,
        stack: value.stack,
        ...('code' in value ? { code: value.code } : {}),
      }
    } else {
      newDetails[key] = value
    }
  })
  return newDetails
}

type LogParams = [message: string, details?: Record<string, unknown>]
type LogFunction = (...args: LogParams) => void
function log(level: 'debug' | 'info' | 'warn' | 'error', ...logArgs: LogParams): void {
  const [message, originalDetails] = logArgs
  const details = normalizeDetails(originalDetails)

  if (datadogLogger != null) {
    datadogLogger[level](message, { details, userId })
  }
  if (logToConsole) {
    // eslint-disable-next-line no-console
    console[level](message, `User#${userId}`, { ...details })
  }
}

const debug: LogFunction = (...args) => log('debug', ...args)
const info: LogFunction = (...args) => log('info', ...args)
const warning: LogFunction = (...args) => log('warn', ...args)
const error: LogFunction = (...args) => log('error', ...args)

export function identifyUser(newUserId: number | null) {
  userId = newUserId
}

export default {
  debug,
  info,
  warning,
  error,
}
